import React, { useState, useRef, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { alpha } from "@mui/material";
import { useTradingColors } from "../tradingColorConfig";

const INTERVALS = [
  { label: "1m", value: "1" },
  { label: "3m", value: "3" },
  { label: "5m", value: "5" },
  { label: "15m", value: "15" },
  { label: "30m", value: "30" },
  { label: "1h", value: "60" },
  { label: "4h", value: "240" },
  { label: "1D", value: "1D" },
  { label: "1W", value: "1W" },
  { label: "1M", value: "1M" },
];

const normalizeAsset = (a) => {
  const cCode = (a?.currencyCode || a?.currencycode || "").toUpperCase();
  const bCode = (a?.baseCurrency || a?.basecurrency || "").toUpperCase();
  return {
    currencyCode: cCode,
    baseCurrency: bCode,
    ltpValue: a?.ltpValue != null ? a.ltpValue : a?.lasttrade1 != null ? a.lasttrade1 : "",
    roc: a?.roc != null ? parseFloat(a.roc) : 0,
    volume: a?.volume || 0,
    currencyType: a?.currencyType,
  };
};

const ChartSlotHeader = ({
  currencycode,
  basecurrency,
  interval = "60",
  onIntervalSelect,
  onSymbolSelect,
  isActive = false,
  btnColor: propBtnColor,
  onActivate,
  onClose,
  showClose = false,
}) => {
  const reduxBtnColor = useSelector((state) => state?.btncolor);
  const themeBtnColor = propBtnColor || reduxBtnColor || "#1976d2";
  const { upColor, downColor } = useTradingColors();
  const [showSymbolSearch, setShowSymbolSearch] = useState(false);
  const [showIntervalMenu, setShowIntervalMenu] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [selectedBaseTab, setSelectedBaseTab] = useState("ALL");
  const [assets, setAssets] = useState([]);

  const searchDropdownRef = useRef(null);
  const symbolBtnRef = useRef(null);
  const intervalDropdownRef = useRef(null);
  const intervalBtnRef = useRef(null);

  // Load all assets from localStorage matching orderbook pairs
  const loadAssets = () => {
    try {
      const cached = localStorage.getItem("spot_assets");
      if (cached) {
        const parsedAssets = JSON.parse(cached);
        if (Array.isArray(parsedAssets) && parsedAssets.length) {
          const valid = parsedAssets
            .map(normalizeAsset)
            .filter((a) => a.currencyCode && a.baseCurrency);
          if (valid.length) {
            setAssets(valid);
            return;
          }
        }
      }
    } catch (e) {
      console.warn("ChartSlotHeader load assets error:", e);
    }

    // Fallback: Fetch from CacheService if spot_assets is not yet cached
    const brokerId = localStorage.getItem("BROKERID");
    if (brokerId) {
      fetch(`https://accounts.paybito.com/CacheService/api/getAssetsData?Name=Assets&BrokerId=${brokerId}`)
        .then((res) => res.json())
        .then((data) => {
          if (data?.value) {
            const filterCurrencyData = JSON.parse(data.value);
            if (Array.isArray(filterCurrencyData?.Values)) {
              localStorage.setItem("spot_assets", JSON.stringify(filterCurrencyData.Values));
              const valid = filterCurrencyData.Values
                .map(normalizeAsset)
                .filter((a) => a.currencyCode && a.baseCurrency);
              setAssets(valid);
            }
          }
        })
        .catch(() => {});
    }
  };

  useEffect(() => {
    loadAssets();
    const handleStorage = (e) => {
      if (!e || e.key === "spot_assets") {
        loadAssets();
      }
    };
    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, []);

  useEffect(() => {
    if (showSymbolSearch) {
      loadAssets();
    }
  }, [showSymbolSearch]);

  // Close dropdowns on outside click
  useEffect(() => {
    const handleOutside = (e) => {
      if (
        searchDropdownRef.current &&
        !searchDropdownRef.current.contains(e.target) &&
        symbolBtnRef.current &&
        !symbolBtnRef.current.contains(e.target)
      ) {
        setShowSymbolSearch(false);
      }
      if (
        intervalDropdownRef.current &&
        !intervalDropdownRef.current.contains(e.target) &&
        intervalBtnRef.current &&
        !intervalBtnRef.current.contains(e.target)
      ) {
        setShowIntervalMenu(false);
      }
    };
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, []);

  // Compute available base currencies for quick filter tabs
  const availableBaseTabs = useMemo(() => {
    const baseSet = new Set();
    assets.forEach((a) => {
      if (a.baseCurrency) baseSet.add(a.baseCurrency);
    });
    const priority = ["USDT", "BTC", "ETH", "USD", "INR", "AED", "EUR"];
    const sortedBases = Array.from(baseSet).sort((a, b) => {
      const ai = priority.indexOf(a);
      const bi = priority.indexOf(b);
      if (ai !== -1 && bi !== -1) return ai - bi;
      if (ai !== -1) return -1;
      if (bi !== -1) return 1;
      return a.localeCompare(b);
    });
    return ["ALL", ...sortedBases];
  }, [assets]);

  // Filter full pairs list by search and/or base tab
  const filteredAssets = useMemo(() => {
    let list = assets;
    const q = searchText.trim().toUpperCase();

    if (!q) {
      if (selectedBaseTab !== "ALL") {
        list = list.filter((a) => a.baseCurrency === selectedBaseTab);
      }
      return list;
    }

    const cleanQ = q.replace(/[\/\-_ ]/g, "");
    return list.filter((a) => {
      const pairStr = `${a.currencyCode}/${a.baseCurrency}`;
      const pairJoined = `${a.currencyCode}${a.baseCurrency}`;

      // If user typed a specific base delimiter, search across all bases
      if (selectedBaseTab !== "ALL" && !q.includes("/") && !q.includes("-") && !q.includes(" ")) {
        if (a.baseCurrency !== selectedBaseTab) return false;
      }

      return (
        pairStr.includes(q) ||
        pairJoined.includes(cleanQ) ||
        a.currencyCode.includes(q) ||
        a.baseCurrency.includes(q)
      );
    });
  }, [assets, searchText, selectedBaseTab]);

  // Find current asset details (for price, ROC)
  const currentAsset = useMemo(() => {
    return assets.find(
      (a) =>
        a.currencyCode?.toUpperCase() === currencycode?.toUpperCase() &&
        a.baseCurrency?.toUpperCase() === basecurrency?.toUpperCase()
    );
  }, [assets, currencycode, basecurrency]);

  const ltp = currentAsset?.ltpValue || "";
  const roc = parseFloat(currentAsset?.roc || 0);
  const isPositive = roc >= 0;

  // Format interval label
  const currentIntervalLabel = useMemo(() => {
    const found = INTERVALS.find((i) => i.value === String(interval));
    return found ? found.label : interval;
  }, [interval]);

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        height: "32px",
        minHeight: "32px",
        padding: "0 8px",
        backgroundColor: "var(--body-color-less2, #181a20)",
        borderBottom: "1px solid rgba(125, 125, 125, 0.15)",
        fontSize: "11px",
        userSelect: "none",
        position: "relative",
        zIndex: 50,
      }}
      onClick={(e) => {
        e.stopPropagation();
        if (onActivate) onActivate();
      }}
    >
      {/* ── LEFT: Symbol & Ticker Stats ── */}
      <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
        {/* Symbol Button */}
        <div style={{ position: "relative" }}>
          <button
            ref={symbolBtnRef}
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setShowSymbolSearch(!showSymbolSearch);
              setShowIntervalMenu(false);
            }}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "4px",
              padding: "2px 6px",
              borderRadius: "4px",
              border: "1px solid rgba(125, 125, 125, 0.2)",
              backgroundColor: showSymbolSearch
                ? alpha(themeBtnColor, 0.12)
                : "rgba(255, 255, 255, 0.04)",
              color: "var(--text-color, #eaecef)",
              fontWeight: 700,
              fontSize: "11px",
              cursor: "pointer",
              transition: "all 0.15s ease",
              outline: "none",
            }}
          >
            <span>
              {currencycode}/{basecurrency}
            </span>
            <svg
              width="8"
              height="5"
              viewBox="0 0 8 5"
              fill="none"
              style={{
                transform: showSymbolSearch ? "rotate(180deg)" : "rotate(0deg)",
                transition: "transform 0.15s ease",
                opacity: 0.7,
              }}
            >
              <path
                d="M1 1L4 4L7 1"
                stroke="currentColor"
                strokeWidth="1.2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>

          {/* Symbol Search Dropdown Popup */}
          {showSymbolSearch && (
            <div
              ref={searchDropdownRef}
              style={{
                position: "absolute",
                top: "calc(100% + 4px)",
                left: 0,
                zIndex: 1500,
                width: "250px",
                maxHeight: "360px",
                backgroundColor: "var(--body-color, #1e2329)",
                border: "1px solid rgba(125, 125, 125, 0.25)",
                borderRadius: "8px",
                boxShadow: "0 10px 25px rgba(0,0,0,0.5)",
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
                backdropFilter: "blur(8px)",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Search Box */}
              <div
                style={{
                  padding: "6px 8px",
                  borderBottom: "1px solid rgba(125, 125, 125, 0.15)",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  backgroundColor: "rgba(0,0,0,0.2)",
                }}
              >
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none" opacity="0.6">
                  <circle cx="5" cy="5" r="3.5" stroke="currentColor" strokeWidth="1.2" />
                  <path d="M7.5 7.5L10.5 10.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
                </svg>
                <input
                  type="text"
                  autoFocus
                  placeholder="Search pair..."
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  style={{
                    flex: 1,
                    background: "transparent",
                    border: "none",
                    outline: "none",
                    color: "var(--text-color, #fff)",
                    fontSize: "11px",
                  }}
                />
                {searchText && (
                  <span
                    onClick={() => setSearchText("")}
                    style={{
                      cursor: "pointer",
                      fontSize: "11px",
                      opacity: 0.6,
                      padding: "0 2px",
                      color: "var(--text-color, #fff)",
                    }}
                  >
                    ✕
                  </span>
                )}
              </div>

              {/* Base Currency Filter Tabs (like orderbook) */}
              {availableBaseTabs.length > 1 && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                    padding: "5px 8px",
                    borderBottom: "1px solid rgba(125, 125, 125, 0.12)",
                    overflowX: "auto",
                    scrollbarWidth: "none",
                    backgroundColor: "rgba(0,0,0,0.1)",
                  }}
                >
                  {availableBaseTabs.map((tab) => {
                    const isTabActive = selectedBaseTab === tab;
                    return (
                      <button
                        key={tab}
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedBaseTab(tab);
                        }}
                        style={{
                          padding: "2px 7px",
                          borderRadius: "10px",
                          border: "none",
                          fontSize: "9.5px",
                          fontWeight: isTabActive ? 700 : 500,
                          backgroundColor: isTabActive
                            ? themeBtnColor
                            : "rgba(255, 255, 255, 0.06)",
                          color: isTabActive ? "#fff" : "rgba(255, 255, 255, 0.7)",
                          cursor: "pointer",
                          whiteSpace: "nowrap",
                          lineHeight: "1.3",
                          transition: "all 0.15s ease",
                        }}
                      >
                        {tab}
                      </button>
                    );
                  })}
                </div>
              )}

              {/* Column Labels */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "4px 10px",
                  fontSize: "9px",
                  color: "rgba(255, 255, 255, 0.4)",
                  borderBottom: "1px solid rgba(125, 125, 125, 0.08)",
                  textTransform: "uppercase",
                  fontWeight: 600,
                  letterSpacing: "0.3px",
                }}
              >
                <span>Pair</span>
                <span>Last Price / 24h</span>
              </div>

              {/* Pair List */}
              <div
                style={{
                  flex: 1,
                  overflowY: "auto",
                  maxHeight: "250px",
                  padding: "2px 0",
                }}
              >
                {filteredAssets.length ? (
                  filteredAssets.map((asset, idx) => {
                    const pairStr = `${asset.currencyCode}/${asset.baseCurrency}`;
                    const isCur =
                      asset.currencyCode?.toUpperCase() === currencycode?.toUpperCase() &&
                      asset.baseCurrency?.toUpperCase() === basecurrency?.toUpperCase();
                    const aRoc = parseFloat(asset.roc || 0);

                    return (
                      <div
                        key={`${asset.currencyCode}-${asset.baseCurrency}-${idx}`}
                        onClick={() => {
                          if (onSymbolSelect) {
                            onSymbolSelect(asset.currencyCode, asset.baseCurrency);
                          }
                          setShowSymbolSearch(false);
                          setSearchText("");
                        }}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          padding: "5px 10px",
                          cursor: "pointer",
                          backgroundColor: isCur ? alpha(themeBtnColor, 0.12) : "transparent",
                          transition: "background-color 0.15s ease",
                        }}
                        onMouseEnter={(e) => {
                          if (!isCur) e.currentTarget.style.backgroundColor = "rgba(255, 255, 255, 0.05)";
                        }}
                        onMouseLeave={(e) => {
                          if (!isCur) e.currentTarget.style.backgroundColor = "transparent";
                        }}
                      >
                        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                          <img
                            src={`https://brokersexchange.s3.us-west-1.amazonaws.com/currency_logo/${asset.currencyCode}.png`}
                            alt=""
                            onError={(e) => {
                              e.target.style.display = "none";
                            }}
                            style={{
                              width: "14px",
                              height: "14px",
                              borderRadius: "50%",
                              objectFit: "contain",
                            }}
                          />
                          <span style={{ fontWeight: 600, color: isCur ? themeBtnColor : "var(--text-color)" }}>
                            {pairStr}
                          </span>
                          <span
                            style={{
                              fontSize: "9px",
                              padding: "0 3px",
                              borderRadius: "2px",
                              backgroundColor: "rgba(125, 125, 125, 0.2)",
                              color: "rgba(255, 255, 255, 0.6)",
                            }}
                          >
                            5x
                          </span>
                        </div>
                        <div style={{ textAlign: "right" }}>
                          <div style={{ fontSize: "10.5px", color: "var(--text-color)", fontWeight: 500 }}>
                            {asset.ltpValue || "-"}
                          </div>
                          <div
                            style={{
                              fontSize: "9.5px",
                              color: aRoc >= 0 ? upColor : downColor,
                            }}
                          >
                            {aRoc >= 0 ? `+${aRoc.toFixed(2)}%` : `${aRoc.toFixed(2)}%`}
                          </div>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div style={{ padding: "12px", textAlign: "center", opacity: 0.5, fontSize: "11px" }}>
                    No pairs found
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* 5x Leverage Tag */}
        <span
          style={{
            fontSize: "9px",
            fontWeight: 700,
            padding: "1px 4px",
            borderRadius: "3px",
            backgroundColor: "rgba(125, 125, 125, 0.18)",
            color: "rgba(255, 255, 255, 0.6)",
            lineHeight: 1.1,
          }}
        >
          5x
        </span>

        {/* Live LTP & ROC */}
        {ltp && (
          <span style={{ fontWeight: 700, color: isPositive ? upColor : downColor }}>
            {ltp}
          </span>
        )}
        {currentAsset?.roc != null && (
          <span
            style={{
              fontSize: "10px",
              color: isPositive ? upColor : downColor,
              opacity: 0.9,
            }}
          >
            {isPositive ? `+${roc.toFixed(2)}%` : `${roc.toFixed(2)}%`}
          </span>
        )}
      </div>

      {/* ── RIGHT: Interval Selector & Slot Controls ── */}
      <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
        {/* Interval Dropdown */}
        <div style={{ position: "relative" }}>
          <button
            ref={intervalBtnRef}
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setShowIntervalMenu(!showIntervalMenu);
              setShowSymbolSearch(false);
            }}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "3px",
              padding: "2px 5px",
              borderRadius: "4px",
              border: "1px solid rgba(125, 125, 125, 0.2)",
              backgroundColor: "rgba(255, 255, 255, 0.04)",
              color: "var(--text-color, #eaecef)",
              fontWeight: 600,
              fontSize: "10px",
              cursor: "pointer",
              outline: "none",
            }}
          >
            <span>{currentIntervalLabel}</span>
            <svg width="6" height="4" viewBox="0 0 6 4" fill="none" opacity="0.7">
              <path d="M1 1L3 3L5 1" stroke="currentColor" strokeWidth="1" strokeLinecap="round" />
            </svg>
          </button>

          {showIntervalMenu && (
            <div
              ref={intervalDropdownRef}
              style={{
                position: "absolute",
                top: "calc(100% + 4px)",
                right: 0,
                zIndex: 1500,
                width: "70px",
                backgroundColor: "var(--body-color, #1e2329)",
                border: "1px solid rgba(125, 125, 125, 0.25)",
                borderRadius: "6px",
                boxShadow: "0 8px 20px rgba(0,0,0,0.45)",
                padding: "3px 0",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {INTERVALS.map((item) => (
                <div
                  key={item.value}
                  onClick={() => {
                    if (onIntervalSelect) onIntervalSelect(item.value);
                    setShowIntervalMenu(false);
                  }}
                  style={{
                    padding: "3px 8px",
                    fontSize: "10.5px",
                    cursor: "pointer",
                    textAlign: "center",
                    fontWeight: String(interval) === item.value ? 700 : 500,
                    color: String(interval) === item.value ? themeBtnColor : "var(--text-color)",
                    backgroundColor:
                      String(interval) === item.value ? alpha(themeBtnColor, 0.12) : "transparent",
                  }}
                  onMouseEnter={(e) => {
                    if (String(interval) !== item.value)
                      e.currentTarget.style.backgroundColor = "rgba(255, 255, 255, 0.06)";
                  }}
                  onMouseLeave={(e) => {
                    if (String(interval) !== item.value)
                      e.currentTarget.style.backgroundColor = "transparent";
                  }}
                >
                  {item.label}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Active Indicator Pin */}
        {isActive && (
          <span
            title="Active chart"
            style={{
              width: "6px",
              height: "6px",
              borderRadius: "50%",
              backgroundColor: themeBtnColor,
              boxShadow: `0 0 4px ${themeBtnColor}`,
            }}
          />
        )}

        {/* Close Button (if custom multi layout supports closing slot) */}
        {showClose && (
          <button
            type="button"
            title="Close this chart slot"
            onClick={(e) => {
              e.stopPropagation();
              if (onClose) onClose();
            }}
            style={{
              background: "transparent",
              border: "none",
              cursor: "pointer",
              color: "rgba(255, 255, 255, 0.4)",
              padding: "0 2px",
              display: "flex",
              alignItems: "center",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.color = "#F5475D")}
            onMouseLeave={(e) => (e.currentTarget.style.color = "rgba(255, 255, 255, 0.4)")}
          >
            ✕
          </button>
        )}
      </div>
    </div>
  );
};

export default ChartSlotHeader;
